import React, { useState, useImperativeHandle, forwardRef, useEffect, useCallback, useRef, useMemo } from 'react'
import { APIProvider, Map, AdvancedMarker, InfoWindow, useMap, useMapsLibrary } from '@vis.gl/react-google-maps'
import Api from '../../_helpers/Api'
import { trans } from '../../_providers/Translation'
import { Maps } from '../../_config/maps'

// images
import closeIcon from '../../assets/img/icons/close.png'
import markerImg from '../../assets/img/leeflet/marker-icon.png'
import reservationMarker from '@/assets/img/schedule/icons/pin.svg'
import greenWorkerImg from '../../assets/img/leeflet/worker_green.png'
import orangeWorkerImg from '../../assets/img/leeflet/worker_orange.png'
import redWorkerImg from '../../assets/img/leeflet/worker_red.png'
import starMarkerIcon from '@/assets/img/schedule/icons/start.svg'
import yellowMarkerImg from '../../assets/img/leeflet/marker-icon-yellow.png'

import '@/assets/scss/groups/modals/employees.scss'
import { useSocketContext } from '../../_providers/Socket'
import { useAuthDataContext } from '../../_providers/Auth'
import Skeleton from 'react-loading-skeleton'
import moment from 'moment'

let listen = false

// Route cache — avoids redundant Directions API calls
let cc = {}

const CENTER = { lat: 42.70060440808085, lng: 23.318481445312504 }


const MAP_STYLES = [
    { featureType: 'poi', stylers: [{ visibility: 'off' }] },
    { featureType: 'transit', stylers: [{ visibility: 'off' }] },
]

function getEmployeeMarkerColor(employee) {
    if (!employee.gps) return "#fd7b7b"
    if (employee.currentreservation) return "#ff8c39"

    return "#73dd57"
}

function convertDistance(km, unit, decimals = 2) {
    if (unit === 'mile') return (km * 0.621371).toFixed(decimals) + ' mi'
    return km.toFixed(decimals) + ' km'
}

// ─── Outer wrapper: provides the Google Maps API context ─────────────────────
function UpcomingReservations(props, ref) {
    return (
        <APIProvider apiKey={Maps.google}>
            <UpcomingReservationsContent {...props} forwardedRef={ref} />
        </APIProvider>
    )
}

// ─── Main content: state, data loading, socket ────────────────────────────────
function UpcomingReservationsContent({ forwardedRef, ...props }) {
    const auth = useAuthDataContext()
    const listener = useSocketContext()

    const [state, setState] = useState({
        overlay: false,
        modal: false,
        loading: false,
        employeeId: null,
        date: null,
        employee: {},
        data: [],
        distances: {},
        selected: null,
        dataLoaded: false,
        fromProps: true,
        update: false,
        refreshFromSocket: false,
    })
    const [currentUnit, setCurrentUnit] = useState('km')
    const [flyToBounds, setFlyToBounds] = useState(true)
    const [fitBounds, setFitBounds] = useState(false)
    const [openInfoWindow, setOpenInfoWindow] = useState(null)

    useImperativeHandle(forwardedRef, () => ({
        open: (_id = null, _date = null) => {
            if (_id) {
                setState(prev => ({ ...prev, employeeId: _id, fromProps: false }))
            }
            if (!props.date) {
                setState(prev => ({ ...prev, date: _date }))
            }
            handleOpen()
        },
        close: () => handleClose(),
    }))

    const loadData = useCallback((fromSocket = false) => {
        if (!state.employeeId) return

        setState(prev => ({
            ...prev,
            loading: fromSocket ? false : true,
            data: fromSocket ? prev.data : [],
            selected: fromSocket ? prev.selected : null,
            distances: fromSocket ? prev.distances : {},
            dataLoaded: false,
        }))

        setFlyToBounds(fromSocket ? false : true)

        let url = 'reservations/upcoming?employee_id=' + state.employeeId
        if (state.date) url += '&start=' + state.date

        Api.get(url)
            .then(res => {
                const employee = res.data.employee || {}
                const reservations = res.data.reservations || []
                const currentReservationId = employee.currentreservation
                    ? Number(employee.currentreservation.id || employee.currentreservation)
                    : null

                setState(prev => ({
                    ...prev,
                    employee,
                    data: reservations,
                    selected: currentReservationId || prev.selected,
                }))
            })
            .finally(() => {
                setState(prev => ({ ...prev, loading: false }))
                setTimeout(() => {
                    setState(prev => ({ ...prev, loading: false, dataLoaded: true }))
                }, 100)
            })
    }, [state.employeeId, state.date, state.update])

    useEffect(() => {
        loadData()
    }, [state.employeeId, state.date])

    // Reset fit state whenever the context changes so the map re-centers
    useEffect(() => {
        setFlyToBounds(true)
        setFitBounds(false)
    }, [state.employeeId, state.date, state.selected])

    useEffect(() => {
        if (state.refreshFromSocket) {
            loadData(true)
        }
    }, [state.refreshFromSocket, loadData])

    const loadCallback = () => {
        setState(prev => ({ ...prev, refreshFromSocket: new Date().getTime() }))
    }

    useEffect(() => {
        if (listen) return

        listener.private(`partners.${auth.partnerId()}`)
            .listen('EmployeePositionChanged', loadCallback)

        listen = true

        return () => {
            setState(prev => ({ ...prev, refreshFromSocket: false }))
            listener.private(`partners.${auth.partnerId()}`).stopListening('EmployeePositionChanged', loadCallback)
            listen = false
        }
    }, [])

    const handleOpen = () => {
        setState(prev => ({ ...prev, overlay: true, update: new Date().getTime() }))
        setTimeout(() => setState(prev => ({ ...prev, modal: true })), 50)
    }

    const handleClose = e => {
        if (e) e.preventDefault()
        setState(prev => ({ ...prev, modal: false }))
        setTimeout(() => setState(prev => ({ ...prev, overlay: false, dataLoaded: false })), 50)
        if (typeof props.onClose === 'function') props.onClose()
    }

    const handleSelect = id => {
        setState(prev => ({ ...prev, selected: prev.selected === id ? null : id }))
        setOpenInfoWindow(prev => prev === `res-${id}` ? null : `res-${id}`)
    }

    const handleDistancesUpdate = useCallback((id, distance, duration) => {
        setState(prev => ({
            ...prev,
            distances: {
                ...prev.distances,
                [Number(id)]: { distance, duration },
            },
        }))
    }, [])

    return (
        <div className={`${state.overlay ? 'visible' : ''} overlay`}>
            <div className={`${state.modal ? 'show' : ''} popup-primary`} style={{ position: "relative" }}>
                <div className="scrollable" style={{ maxHeight: '65vh' }}>
                    <div id="map-wrapper">
                        <div className='close-container'>
                            <span className='close' onClick={handleClose}>

                            </span>
                        </div>
                        <div className="left-new">
                            <div className='top'>
                                <span className='icon'>
                                    <img src={state.employee.avatar} alt='avatar' />
                                </span>
                                <div className='about'>
                                    <b>
                                        {state.employee.name} {state.employee.lastname}
                                    </b>
                                    <p>
                                        Предстоящи резервации ({state.data.length}) ~ {convertDistance(state.data.reduce((sum, r) => sum + (state.distances[Number(r.id)]?.distance || 0), 0), currentUnit, 1)}
                                    </p>
                                </div>
                                {state.date ?
                                    <div className='calendar'>
                                        <strong>
                                            {moment(state.date).format('DD')}
                                        </strong>
                                        <span>
                                            {moment(state.date).format('MMM')}
                                        </span>
                                    </div>
                                    :
                                    <>
                                    </>
                                }
                            </div>
                            <hr />
                            {state.loading
                                ? <Skeleton style={{ minHeight: '400px' }} />
                                : state.data.length === 0
                                    ? <div className="alert info">{trans('messages.noReservations')}</div>
                                    : <ul>
                                        {state.data.map((r, i) =>
                                            <li
                                                key={'r-' + r.id}
                                                onClick={() => handleSelect(r.id)}
                                                className={state.selected === r.id ? 'active' : ''}
                                            >
                                                <div className='top'>
                                                    <div className='left'>
                                                        <b>
                                                            {`# ${r.reference_code}`}
                                                        </b>
                                                        <p>
                                                            {r.full_address}
                                                        </p>
                                                    </div>
                                                    <span className='status'>
                                                        {r.end_work_at
                                                            ? trans('random.endWorks')
                                                            : r.start_work_at
                                                                ? trans('random.startWorks')
                                                                : r.is_employee_travels
                                                                    ? trans('random.travels')
                                                                    : trans('random.notTravels')
                                                        }
                                                    </span>
                                                </div>
                                                {state.selected === r.id ?
                                                    <>
                                                        <hr />
                                                        <table>
                                                            <tbody>
                                                                <tr>
                                                                    <td>{trans('random.visitHour')}:</td>
                                                                    <td>{r.visitperiod ? r.visitperiod.period : '-'}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td>{trans('random.client')}:</td>
                                                                    <td>{r.customer.name} {r.customer.lastname}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td>{trans('random.phone')}:</td>
                                                                    <td>{r.customer.phone}</td>
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                        <hr />
                                                        <table>
                                                            <tbody>
                                                                <tr>
                                                                    <td>{trans('random.category')}:</td>
                                                                    <td>{r.appliancecategory ? r.appliancecategory.name : '-'}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td>{trans('random.make')}:</td>
                                                                    <td>{r.appliancebrand ? r.appliancebrand.name : '-'}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td>{trans('random.model')}:</td>
                                                                    <td>{r.appliancemodel ? r.appliancemodel.name : '-'}</td>
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                        {auth.module('gps') ?
                                                            <>
                                                                <hr />
                                                                <table>
                                                                    <tbody>
                                                                        <tr>
                                                                            <td>{trans('random.distanceTime')}:</td>
                                                                            <td>
                                                                                {state.distances[Number(r.id)]
                                                                                    ? convertDistance(state.distances[Number(r.id)].distance, currentUnit)
                                                                                    + ' / '
                                                                                    + Math.ceil(state.distances[Number(r.id)].duration) + ' ' + trans('random.min')
                                                                                    : '- / -'}
                                                                            </td>
                                                                        </tr>
                                                                        <tr>
                                                                            <td>{trans('random.remainingDistanceTime')}:</td>
                                                                            <td style={{ whiteSpace: 'nowrap' }}>
                                                                                {r.remaining_distance ? convertDistance(r.remaining_distance, currentUnit) : '-'} / {r.remaining_time_to_visit ? (Math.ceil(r.remaining_time_to_visit) + ' ' + trans('random.min')) : '-'}
                                                                            </td>
                                                                        </tr>
                                                                    </tbody>
                                                                </table>
                                                            </>
                                                            :
                                                            <>
                                                            </>
                                                        }
                                                    </>
                                                    :
                                                    <>
                                                    </>
                                                }
                                            </li>
                                        )}
                                    </ul>
                            }
                        </div>

                        <div className="right">
                            <div className="map">
                                <Map
                                    defaultCenter={CENTER}
                                    defaultZoom={10}
                                    mapId="DEMO_MAP_ID"
                                    styles={MAP_STYLES}
                                    style={{ width: '100%', height: '100%' }}
                                    disableDefaultUI={true}
                                    keyboardShortcuts={false}
                                >
                                    <MapOverlays
                                        employee={state.employee}
                                        reservations={state.data}
                                        selected={state.selected}
                                        flyToBounds={flyToBounds}
                                        fitBounds={fitBounds}
                                        setFitBounds={setFitBounds}
                                        openInfoWindow={openInfoWindow}
                                        setOpenInfoWindow={setOpenInfoWindow}
                                        onDistancesUpdate={handleDistancesUpdate}
                                        overlay={state.overlay}
                                        modal={state.modal}
                                    />
                                </Map>
                                <ul className='current-unit'>
                                    <li className={currentUnit === 'km' ? 'active' : ''} onClick={() => setCurrentUnit('km')}>
                                        km
                                    </li>
                                    <li className={currentUnit === 'mile' ? 'active' : ''} onClick={() => setCurrentUnit('mile')}>
                                        mile
                                    </li>
                                </ul>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    )
}

// ─── Map overlays (must live inside <Map> context) ────────────────────────────
function MapOverlays({ employee, reservations, selected, flyToBounds, fitBounds, setFitBounds, openInfoWindow, setOpenInfoWindow, onDistancesUpdate, overlay, modal }) {
    const map = useMap()
    const routesLib = useMapsLibrary('routes')
    const auth = useAuthDataContext()
    const directionsRenderersRef = useRef([])
    const dashedRouteRenderersRef = useRef([])

    // Parse employee position (only when GPS module is active)
    const employeePos = useMemo(() => {
        if (!auth.module('gps') || !employee?.lat || !employee?.lon) return null
        const lat = parseFloat(employee.lat)
        const lng = parseFloat(employee.lon)
        if (!isFinite(lat) || !isFinite(lng)) return null
        return { lat, lng }
    }, [employee])

    // All reservation markers with parsed numeric coords
    const reservationMarkers = useMemo(() => (
        reservations
            .filter(r => r.lat && r.lon)
            .map(r => ({
                ...r,
                lat: parseFloat(r.lat),
                lng: parseFloat(r.lon),
            }))
    ), [reservations])

    // The currently selected reservation
    const selectedReservation = useMemo(() => (
        reservationMarkers.find(r => Number(r.id) === Number(selected)) || null
    ), [reservationMarkers, selected])

    // Predict marker — the reservation whose ID matches start_id of the selected one
    const predictMarker = useMemo(() => {
        if (!selectedReservation?.start_id) return null
        return reservationMarkers.find(r => Number(r.id) === Number(selectedReservation.start_id)) || null
    }, [selectedReservation, reservationMarkers])

    // Start marker — the employee's position stored on the selected reservation
    const startMarker = useMemo(() => {
        if (!selectedReservation) return null
        const lat = parseFloat(selectedReservation.employee_lat)
        const lng = parseFloat(selectedReservation.employee_lon)
        if (!isFinite(lat) || !isFinite(lng)) return null
        return { lat, lng }
    }, [selectedReservation])

    // Show all reservations when nothing is selected; only the selected one otherwise
    const visibleReservations = useMemo(() => (
        selected
            ? reservationMarkers.filter(r => Number(r.id) === Number(selected))
            : reservationMarkers
    ), [reservationMarkers, selected])

    // Fit viewport to all currently relevant markers
    useEffect(() => {
        if (!map || (!flyToBounds && fitBounds)) return

        const points = []
        if (employeePos) points.push(employeePos)

        if (selected) {
            if (selectedReservation) points.push({ lat: selectedReservation.lat, lng: selectedReservation.lng })
            if (predictMarker) points.push({ lat: predictMarker.lat, lng: predictMarker.lng })
            if (startMarker) points.push(startMarker)
        } else {
            reservationMarkers.forEach(r => points.push({ lat: r.lat, lng: r.lng }))
        }

        if (points.length === 0) return

        if (points.length === 1) {
            map.setCenter(points[0])
            map.setZoom(14)
        } else {
            const bounds = new window.google.maps.LatLngBounds()
            points.forEach(p => bounds.extend(p))
            map.fitBounds(bounds, 50)
        }

        setFitBounds(true)
    }, [map, flyToBounds, fitBounds, employeePos, visibleReservations, predictMarker, startMarker])

    // Pan to the selected reservation marker
    useEffect(() => {
        if (!map || !selectedReservation) return
        map.panTo({ lat: selectedReservation.lat, lng: selectedReservation.lng })
    }, [map, selected])

    // Draw solid route from startMarker to employee current position
    useEffect(() => {
        if (!map || !routesLib) return

        directionsRenderersRef.current.forEach(dr => dr.setMap(null))
        directionsRenderersRef.current = []

        if (!startMarker || !employeePos) return

        const cacheKey = `solid-${startMarker.lat}${startMarker.lng}${employeePos.lat}${employeePos.lng}`

        const dr = new routesLib.DirectionsRenderer({
            map,
            suppressMarkers: true,
            preserveViewport: true,
        })

        const applyRoute = result => {
            dr.setDirections(result)
            directionsRenderersRef.current.push(dr)
        }

        if (cc[cacheKey]) {
            applyRoute(cc[cacheKey])
            return
        }

        const ds = new routesLib.DirectionsService()
        ds.route(
            { origin: startMarker, destination: employeePos, travelMode: routesLib.TravelMode.DRIVING },
            (result, status) => {
                if (status === routesLib.DirectionsStatus.OK) {
                    cc[cacheKey] = result
                    applyRoute(result)
                }
            }
        )

        return () => {
            directionsRenderersRef.current.forEach(dr => dr.setMap(null))
        }
    }, [map, routesLib, startMarker, employeePos])

    // Draw dashed route from employee current position to the reservation markers (blue)
    useEffect(() => {
        if (!map || !routesLib) return

        dashedRouteRenderersRef.current.forEach(dr => dr.setMap(null))
        dashedRouteRenderersRef.current = []

        if (!employeePos || reservationMarkers.length === 0) return

        const ds = new routesLib.DirectionsService()

        reservationMarkers.forEach(r => {
            const dest = { lat: r.lat, lng: r.lng }
            const cacheKey = `dashed-${employeePos.lat}${employeePos.lng}${dest.lat}${dest.lng}`
            const shouldRender = visibleReservations.some(v => Number(v.id) === Number(r.id))

            const applyRoute = result => {
                if (shouldRender) {
                    const dr = new routesLib.DirectionsRenderer({
                        map,
                        suppressMarkers: true,
                        preserveViewport: true,
                        polylineOptions: {
                            strokeOpacity: 0,
                            icons: [{
                                icon: {
                                    path: 'M 0,-1 0,1',
                                    strokeOpacity: 1,
                                    strokeColor: '#3A61FF',
                                    strokeWeight: 3,
                                    scale: 4,
                                },
                                offset: '0',
                                repeat: '16px',
                            }],
                        },
                    })
                    dr.setDirections(result)
                    dashedRouteRenderersRef.current.push(dr)
                }

                const leg = result.routes[0]?.legs[0]
                if (leg) {
                    onDistancesUpdate(
                        r.id,
                        leg.distance.value / 1000,
                        leg.duration.value / 60,
                    )
                }
            }

            if (cc[cacheKey]) {
                applyRoute(cc[cacheKey])
                return
            }

            ds.route(
                { origin: employeePos, destination: dest, travelMode: routesLib.TravelMode.DRIVING },
                (result, status) => {
                    if (status === routesLib.DirectionsStatus.OK) {
                        cc[cacheKey] = result
                        applyRoute(result)
                    }
                }
            )
        })

        return () => {
            dashedRouteRenderersRef.current.forEach(dr => dr.setMap(null))
        }
    }, [map, routesLib, employeePos, reservationMarkers, visibleReservations])

    // Trigger resize when the modal opens so the map fills its container
    useEffect(() => {
        if (!map || !overlay || !modal) return
        window.google.maps.event.trigger(map, 'resize')
    }, [map, overlay, modal])

    return (
        <>
            {/* Employee current position marker */}
            {employeePos && (
                <>
                    <AdvancedMarker
                        position={employeePos}
                        onClick={() => setOpenInfoWindow('employee')}
                    >
                        <div style={{
                            width: 44,
                            height: 44,
                            borderRadius: '50%',
                            overflow: 'hidden',
                            border: `3px solid ${getEmployeeMarkerColor(employee)}`,
                            boxShadow: '0 2px 6px rgba(0,0,0,0.35)',
                            backgroundColor: '#ffe6cc',
                            flexShrink: 0,
                        }}>
                            <img
                                src={employee.avatar}
                                alt="employee"
                                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                            />
                        </div>
                    </AdvancedMarker>
                    {openInfoWindow === 'employee' && (
                        <InfoWindow
                            position={employeePos}
                            onCloseClick={() => setOpenInfoWindow(null)}
                        >
                            <div>{trans('random.currentPosition')}</div>
                        </InfoWindow>
                    )}
                </>
            )}

            {/* Reservation markers (blue) */}
            {visibleReservations.map(r => {
                const pos = { lat: r.lat, lng: r.lng }
                const key = `res-${r.id}`
                return (
                    <React.Fragment key={key}>
                        <AdvancedMarker position={pos} onClick={() => setOpenInfoWindow(key)}>
                            <img src={reservationMarker} width={20} height={23} alt="reservation" />
                        </AdvancedMarker>
                        {openInfoWindow === key && (
                            <InfoWindow
                                position={pos}
                                onCloseClick={() => setOpenInfoWindow(null)}
                                headerContent={<b style={{ fontFamily: 'Inter Bold' }}>{`#${r.reference_code}`}</b>}
                            >
                                <div style={{ fontSize: 13, lineHeight: 1.6 }}>
                                    <table>
                                        <tbody>
                                            <tr>
                                                <td>{trans('random.visitHour')}:</td>
                                                <td>{r.visitperiod ? r.visitperiod.period : '-'}</td>
                                            </tr>
                                            <tr>
                                                <td>{trans('random.address')}:</td>
                                                <td>{r.full_address}</td>
                                            </tr>
                                            <tr>
                                                <td>{trans('random.client')}:</td>
                                                <td>{r.customer ? `${r.customer.name} ${r.customer.lastname}` : '-'}</td>
                                            </tr>
                                            <tr>
                                                <td>{trans('random.phone')}:</td>
                                                <td>{r.customer?.phone || '-'}</td>
                                            </tr>
                                            <tr>
                                                <td>{trans('random.category')}:</td>
                                                <td>{r.appliancecategory ? r.appliancecategory.name : '-'}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </InfoWindow>
                        )}
                    </React.Fragment>
                )
            })}

            {/* Predict marker — expected technician position (avatar circle) */}
            {predictMarker && (
                <>
                    <AdvancedMarker
                        position={{ lat: predictMarker.lat, lng: predictMarker.lng }}
                        onClick={() => setOpenInfoWindow('predict')}
                    >
                        <div style={{
                            width: 44,
                            height: 44,
                            borderRadius: '50%',
                            overflow: 'hidden',
                            border: '3px solid #fff',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.35)',
                            backgroundColor: '#ffe6cc',
                            flexShrink: 0,
                        }}>
                            <img
                                src={employee.avatar}
                                alt="employee"
                                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                            />
                        </div>
                    </AdvancedMarker>
                    {openInfoWindow === 'predict' && (
                        <InfoWindow
                            position={{ lat: predictMarker.lat, lng: predictMarker.lng }}
                            onCloseClick={() => setOpenInfoWindow(null)}
                            headerContent={<b style={{ fontFamily: "Inter Bold" }}>{`${employee.name} ${employee.lastname}`}</b>}
                        >
                            <div>{trans('random.expectingPosition')}</div>
                        </InfoWindow>
                    )}
                </>
            )}

            {startMarker && (
                <AdvancedMarker
                    position={startMarker}
                    style={{
                        cursor: 'default'
                    }}
                >
                    <img src={starMarkerIcon} width={25} height={41} alt="start position" />
                </AdvancedMarker>
            )}
        </>
    )
}

export default React.memo(forwardRef(UpcomingReservations), (prev, next) => {
    return JSON.stringify(prev) === JSON.stringify(next)
})
